import React, { useState, useEffect, useCallback } from "react";
import {
  useAppUserManager,
  useOrgManager,
  usePayoutManager,
  useSchoolManager,
  useDistrictManager,
} from "../../../../hooks/useManagers";
import { SelectPicker } from "rsuite";
import MainLayout from "../../../../components/layout/MainLayout";
import ButtonControl from "../../../../components/controls/ButtonControl";
import PayoutAdminRecipients from "./PayoutAdminRecipients";
import PayoutAdminSettings from "./PayoutAdminSettings";
import PayoutFilter from "./PayoutFilter";
import PayoutTable from "./PayoutTable";
import { useParams, useLocation } from "react-router-dom";
import { useNavigate } from "../../../../components/Navigate";
import Loading from "../../../../components/Loading";
import { ReportingService } from "../../../../services/ReportingService";
import { useHookstate } from "@hookstate/core";
import globalAuthStateStore from "../../../../stores/globalAuthStateStore";
import { DialogControl } from "../../../../components/controls/DialogControl";
import { SelectListControl } from "../../../../components/controls";

const PayoutAdminMgmt = () => {
  const location = useLocation();
  const params = useParams();
  const schoolMgr = useSchoolManager();
  const districtMgr = useDistrictManager();
  const navigate = useNavigate();
  const payoutMgr = usePayoutManager();
  const orgMgr = useOrgManager();
  const authStore = useHookstate(globalAuthStateStore);
  var reportingSvc = new ReportingService(authStore);
  const appUserMgr = useAppUserManager();
  const [orgs, setOrgs] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [errors, setErrors] = useState(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("");
  const [results, setResults] = useState([]);
  const [showRecipientTable, setShowRecipientTable] = useState(false);
  const [showPayoutSettings, setShowPayoutSettings] = useState(false);
  const [data, setData] = useState();
  const [recipientResults, setRecipientResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [payoutAdminConfigMetricsData, setPayoutAdminConfigMetricsData] =
    useState();
  const [payoutAdminConfig, setPayoutAdminConfig] = useState();
  const [exportTypeDialog, setExportTypeDialog] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState("PDF");
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [payoutId, setPayoutId] = useState(null);
  const [payoutSchoolItems, setPayoutSchoolItems] = useState([]);

  const loadRecipientData = useCallback(async () => {
    const clientId = payoutInfo?.ClientId;
    let districtId = payoutInfo?.DistrictId;
    let payoutRecipients;
    let errors = false;
    let result;
    const payoutSchoolId = params?.payoutId;

    try {
      if (!payoutInfo || payoutInfo.PayoutStatus == "Not Started") {
        result = await payoutMgr.LoadPayoutSchools(
          payoutSchoolId,
          undefined,
          districtId,
          1
        );
        if (!districtId && result?.length > 0) {
          districtId = result.first().DistrictId;
        }
      }

      const isCompleted = result?.first()?.PayoutStatus
        ? result?.first()?.PayoutStatus == 3
        : payoutInfo?.PayoutStatus == 3 ||
          payoutInfo?.PayoutStatus === "Completed";

      const payoutAdminsResponse = await payoutMgr.GetPayoutAdmins(
        clientId,
        districtId,
        false,
        isCompleted
      );

      if (payoutAdminsResponse.Success) {
        payoutRecipients = payoutAdminsResponse.Items;
      }

      if (!payoutAdminConfig && !payoutAdminConfigMetricsData) {
        const response = await payoutMgr.GetAdminPayoutConfiguration(
          payoutAdminsResponse?.Items?.first().ClientId,
          payoutAdminsResponse?.Items?.first().DistrictId,
          payoutAdminsResponse?.Items?.first().SchoolId
        );

        if (response.Items?.length > 0) {
          const metricSet = response.Items[0].PayoutAdminConfigurationMetric;
          setPayoutAdminConfigMetricsData(metricSet);
          setPayoutAdminConfig(response.Items[0]);
        }
      } else if (!payoutAdminsResponse.Success) {
        errors = true;
      }

      const schoolsResult = await payoutMgr.LoadPayoutSchools(
        undefined,
        undefined,
        districtId,
        1
      );
      const districtItems = Array.isArray(schoolsResult)
        ? schoolsResult.filter((r) => r.DistrictId === districtId)
        : [];

      if (districtItems.length > 0) {
        setPayoutSchoolItems(districtItems);
        if (!payoutInfo) {
          setPayoutInfo(districtItems.first());
        }
      } else {
        setErrors(["No payout records found for this district"]);
        setPayoutSchoolItems([]);
      }

      if (!errors) {
        setRecipientResults(payoutRecipients);
      }
    } catch (error) {
      console.error("Error loading recipient data:", error);
      setErrors(["Failed to load recipient data"]);
      errors = true;
    } finally {
      setIsLoading(false);
    }
  }, [
    payoutInfo,
    params?.payoutId,
    payoutMgr,
    payoutAdminConfig,
    payoutAdminConfigMetricsData,
    navigate,
    setErrors,
    setIsLoading,
    setPayoutAdminConfigMetricsData,
    setPayoutAdminConfig,
    setPayoutInfo,
    setPayoutSchoolItems,
    setRecipientResults,
  ]);

  const loadPayoutConfig = useCallback(
    async (payoutConfigId) => {
      try {
        if (!payoutAdminConfig) {
          const config = await payoutMgr._payoutAdminConfigurationItemMgr.query(
            "x => x.Id == payoutConfigId",
            { payoutConfigId }
          );

          if (config.Items.length > 0) {
            const configItem = config.Items.first();
            const response = await payoutMgr.GetAdminPayoutConfiguration(
              configItem.ClientId,
              configItem.DistrictId,
              configItem.SchoolId
            );

            if (response.Items?.length > 0) {
              setPayoutAdminConfig(response.Items[0]);
            }

            const schoolsResult = await payoutMgr.LoadPayoutSchools(
              undefined,
              configItem.SchoolId,
              configItem.DistrictId,
              1
            );

            if (schoolsResult && schoolsResult.length > 0) {
              setPayoutSchoolItems(schoolsResult);
              setPayoutInfo(schoolsResult.first());
            } else {
              setErrors(["No payout records found for this district"]);
              setPayoutSchoolItems([]);
            }
          }
        }
      } catch (error) {
        console.error("Error loading payout config:", error);
        setErrors(["Failed to load payout configuration"]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      payoutAdminConfig,
      payoutMgr,
      setPayoutAdminConfig,
      setPayoutInfo,
      setPayoutSchoolItems,
      setErrors,
      setIsLoading,
    ]
  );

  const handleDataProperties = useCallback(() => {
    setIsLoading(true);

    if (!payoutMgr || !appUserMgr || !orgMgr) return;

    if (params?.payoutConfigId) {
      setShowRecipientTable(false);
      setShowPayoutSettings(true);
      loadPayoutConfig(params.payoutConfigId);
    } else if (params?.payoutId) {
      setShowRecipientTable(true);
      setPayoutId(params.payoutId);
      setShowPayoutSettings(false);
      loadRecipientData();
    } else {
      setShowRecipientTable(false);
      setShowPayoutSettings(false);
      setPayoutAdminConfigMetricsData(null);
      setPayoutAdminConfig(null);

      if (!orgs) {
        orgMgr.loadOrgTreeWithPayouts("district").then((items) => {
          setOrgs(items);
        });
      }

      payoutMgr
        .LoadPayoutSchools(undefined, undefined, undefined, 1)
        .then((r) => {
          if (Array.isArray(r)) {
            setData(r);
            setIsLoading(false);
          } else {
            setIsLoading(false);
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [
    location.pathname,
    params,
    payoutMgr,
    appUserMgr,
    orgMgr,
    orgs,
    loadRecipientData,
    loadPayoutConfig,
    setIsLoading,
    setShowRecipientTable,
    setShowPayoutSettings,
    setPayoutId,
    setPayoutAdminConfigMetricsData,
    setPayoutAdminConfig,
    setOrgs,
    setData,
  ]);

  useEffect(() => {
    handleDataProperties();
  }, [params, location, payoutMgr, appUserMgr]);

  const handleCalculatePayout = (
    schoolId,
    schoolYearIdentifier,
    districtId,
    fileType
  ) => {
    setLoading(true);
    payoutMgr
      .CalculatePayout(schoolId, schoolYearIdentifier, districtId, true)
      .then((response) => {
        if (response.Success) {
          setResults(response);
          handleOnCalculate(districtId, schoolId, fileType);
        } else {
          setLoading(false);
          setErrors([
            "Failed to calculate payout. If this problem persist, please contact customer support.",
          ]);
        }
      });
  };

  const handleOnCalculate = async (districtId, schoolId, fileType) => {
    setLoading(true);
    const reportParams = {
      report: "SchoolAdministratorPayout.trdp",
      parameterValues: {
        CurrentUserId: null,
        CurrentUserTypeName: "AllData",
        DateRangeEnd: null,
        DateRangeStart: null,
        SchoolYear: parseInt(payoutMgr.AppUserState.selectedSchoolYear),
        DistrictID: districtId,
      },
    };

    const result = await reportingSvc.getExport(
      reportParams,
      fileType,
      exportTypeDialog.type
    );
    setLoading(false);
    if (result) {
      setErrors([
        "Calculations Succeeded, but unable to generate Payout Export.",
      ]);
    }
    setExportTypeDialog(false);
  };

  const handleOnEdit = async (schoolInfo) => {
    setIsLoading(true);
    setPayoutInfo(schoolInfo);
    navigate(`/admin/Payout/Admin/${schoolInfo.Id}`);
  };

  const handleOnRecipientNext = async (payoutRecipientsToSave) => {
    setIsLoading(true);
    await savePayoutUsers(payoutRecipientsToSave);
    if (appUserMgr.canWrite("ConfigurePayoutAdminConfiguration")) {
      setShowPayoutSettings(true);
      setShowRecipientTable(false);
      navigate(`/admin/Payout/Admin/Configuration/${payoutAdminConfig.Id}`);
    } else {
      setShowRecipientTable(true);
      navigate(`/admin/Payout/Admin`);
    }
  };

  const savePayoutUsers = async (
    payoutRecipientsToSave,
    forceSave,
    isComplete
  ) => {
    if (payoutRecipientsToSave.length > 0) {
      const arrayOfPromises = [];
      for (const rowIndex of payoutRecipientsToSave) {
        populateOriginalWithCustom(rowIndex);
        if (rowIndex.isEdited || forceSave) {
          if (isComplete) rowIndex.CompletedDate = new Date();
          const recipientSaveResult = arrayOfPromises.push(
            payoutMgr._payoutAdminItemMgr.save(rowIndex)
          );
          if (recipientSaveResult) {
            console.log("save " + recipientSaveResult);
          }
        }
      }
      const results = await Promise.all(arrayOfPromises);
      if (results.every((x) => x.Success)) {
        return true;
      } else {
        setErrors(["Failed To save a recipient"]);
      }
    }
  };

  const populateOriginalWithCustom = (row) => {
    for (const key in row) {
      const adminOriginalCounterPart =
        key.startsWith("Custom") ||
        key === "Comment" ||
        key === "IsActive" ||
        key === "IsAssistantPrincipal" ||
        key === "PayoutPct" ||
        key === "PayoutPctReason" ||
        key === "SKRScore" ||
        key === "SurveyScore";

      if (
        adminOriginalCounterPart &&
        row[key] !== null &&
        row[key] !== undefined &&
        row["Original" + key] !== row[key] &&
        (row["Original" + key] === null ||
          row["Original" + key] === undefined ||
          row["Original" + key] === "") &&
        row["Original" + key] >= 0 &&
        row[key] >= 0
      ) {
        row["Original" + key] = row[key];
        row.isEdited = true;
      }
    }
  };

  const handleOnRecipientPrevious = async () => {
    setIsLoading(true);
    setRecipientResults([]);
    navigate(-1);
  };

  const handleOnExport = async (row, fileType) => {
    const reportParams = {
      report: "SchoolAdministratorPayoutUsers.trdp",
      parameterValues: {
        DateRangeEnd: null,
        DateRangeStart: null,
        SchoolYear: row.SchoolYearIdentifier,
        DistrictId: row.DistrictId,
        OrganizationId: row.ClientId,
      },
    };

    setLoading(true);
    const result = await reportingSvc.getExport(
      reportParams,
      fileType,
      exportTypeDialog.type
    );
    setLoading(false);
    setExportTypeDialog(false);
  };

  const handleOnComplete = async () => {
    if (payoutSchoolItems.length === 0) {
      setErrors(["No payout records found for this district"]);
      return;
    }

    savePayoutUsers(recipientResults, true, true);

    const savePromises = payoutSchoolItems.map((item) =>
      payoutMgr.savePayoutSchool({
        ...item,
        PayoutStatus: 3,
        CompletedDate: new Date(),
      })
    );

    await Promise.all(savePromises);
    navigate("/admin/Payout/Admin");
  };

  const handleOnPaymentSettingsPrevious = () => {
    setIsLoading(true);
    navigate(`/admin/Payout/Admin/${payoutInfo.Id}`);
  };

  const handleOnPaymentSettingsSave = () => {
    payoutMgr
      .SavePayoutSettings(selectedSchool, selectedYear)
      .then((response) => {
        setShowPayoutSettings(false);
      });
  };

  const handleOnFilterChange = async (field, value) => {
    const filter = payoutMgr._payoutFilter;
    const updatedFilter = { ...filter };

    if (field === "District") {
      updatedFilter.District = value;
    } else if (field === "School") {
      updatedFilter.School = value;
    } else if (field === "Status") {
      updatedFilter.Status = value;
    } else if (field === "Group") {
      updatedFilter.Group = value;
    }
    setData(await payoutMgr.FilterPayouts(updatedFilter));
  };

  return (
    <>
      <DialogControl
        title={"Calculate as Admin Payout"}
        innerclassName={"dialog__inner__center"}
        openDialog={exportTypeDialog}
        okText={"Generate"}
        loadingOk={loading}
        enableNext={true}
        onCancel={() => {
          setExportTypeDialog(false);
        }}
        onOk={() => {
          if (selectedFileType === "PDF") {
            if (params.payoutConfigId) {
              handleCalculatePayout(
                payoutInfo.School?.Id,
                payoutInfo.SchoolYearIdentifier,
                payoutInfo?.DistrictId,
                "PDF"
              );
            } else {
              handleCalculatePayout(
                exportTypeDialog.item.School?.Id,
                exportTypeDialog.item.SchoolYearIdentifier,
                exportTypeDialog.item.DistrictId,
                "PDF"
              );
            }
          } else if (selectedFileType === "EXCEL") {
            if (params.payoutConfigId) {
              handleCalculatePayout(
                payoutInfo.School?.Id,
                payoutInfo.SchoolYearIdentifier,
                payoutInfo?.DistrictId,
                "XLSX"
              );
            } else {
              handleCalculatePayout(
                exportTypeDialog.item.School?.Id,
                exportTypeDialog.item.SchoolYearIdentifier,
                exportTypeDialog.item.DistrictId,
                "XLSX"
              );
            }
          }
        }}
      >
        <SelectListControl
          field={{ fieldName: "filetype" }}
          value={selectedFileType}
          hidePleaseSelect={true}
          textValuePairs={[
            { text: "PDF", value: "PDF" },
            { text: "EXCEL", value: "EXCEL" },
          ]}
          onChange={(e) => {
            setSelectedFileType(e.target.value);
          }}
        />
      </DialogControl>
      <MainLayout errors={errors} validationErrors={validationErrors}>
        <>
          {!showRecipientTable && !showPayoutSettings ? (
            <div className="screen-wrapper">
              <h2>{isLoading ? "" : "Admin Payout"}</h2>
              <div className="control-box-wrapper">
                <div className="control-box-list">
                  {isLoading ? (
                    <div className="payout-page-loading">
                      <Loading type="default" size="5rem" />
                      <div className="loading-font">Loading</div>
                    </div>
                  ) : (
                    <>
                      <PayoutFilter
                        orgs={orgs}
                        orgMgr={orgMgr}
                        isAdmin={true}
                        onFilterChange={handleOnFilterChange}
                        filterData={data}
                        filter={payoutMgr?._payoutFilter}
                      />
                      <PayoutTable
                        appUserMgr={appUserMgr}
                        isAdmin={true}
                        filteredItems={data}
                        onEdit={handleOnEdit}
                        onExport={(row) =>
                          setExportTypeDialog({
                            item: row,
                            type: "Admin User Export",
                          })
                        }
                        onCalculate={(row) =>
                          setExportTypeDialog({
                            item: row,
                            type: "Admin Payout Export",
                          })
                        }
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : isLoading ? (
            <div className="payout-page-loading ">
              <Loading type="default" size="5rem" />
              <div className="loading-font">Loading...</div>
            </div>
          ) : showRecipientTable ? (
            <PayoutAdminRecipients
              isAdmin={true}
              data={recipientResults}
              payoutInfo={payoutInfo}
              metricData={payoutAdminConfigMetricsData}
              onNext={handleOnRecipientNext}
              onPrevious={handleOnRecipientPrevious}
              appUserMgr={appUserMgr}
              onExport={() =>
                setExportTypeDialog({
                  item: payoutInfo,
                  type: "Admin User Export",
                })
              }
            />
          ) : showPayoutSettings ? (
            <PayoutAdminSettings
              recipientResults={recipientResults}
              payoutMgr={payoutMgr}
              payoutInfo={payoutInfo}
              payoutConfig={payoutAdminConfig}
              setPayoutAdminConfigMetricsData={setPayoutAdminConfigMetricsData}
              setPayoutConfig={setPayoutAdminConfig}
              onPrevious={handleOnPaymentSettingsPrevious}
              onCaluclate={() =>
                setExportTypeDialog({
                  item: payoutInfo,
                  type: "Admin Payout Export",
                })
              }
              handleOnComplete={handleOnComplete}
              appUserMgr={appUserMgr}
              savePayoutUsers={savePayoutUsers}
              setValidationErrors={setValidationErrors}
              isCompleteDisabled={payoutSchoolItems.length === 0}
            />
          ) : null}
        </>
      </MainLayout>
    </>
  );
};

export default PayoutAdminMgmt;